/**
 * Error Reporting Configuration (Story 03-03)
 *
 * Frontend tunables for `lib/error-reporter.ts`. Parses optional `VITE_*` env
 * vars with a safe fallback (never throws on a missing/invalid value) — same
 * spirit as the backend's `error-log.config.ts`, simplified for the client.
 */

const DEFAULT_FLUSH_MS = 15_000;
const DEFAULT_MAX_PER_SESSION = 10;
const DEFAULT_MAX_STACK_BYTES = 4_000;
/** Stays comfortably under the backend route's 32 KB body limit (`errors.route.ts`). */
const DEFAULT_MAX_BATCH_BYTES = 28_000;

function parsePositiveInt(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/** Flush the pending batch at most this often (ms), in addition to visibilitychange/pagehide. */
export const ERROR_LOG_FLUSH_MS = parsePositiveInt(
  import.meta.env.VITE_ERROR_LOG_FLUSH_MS,
  DEFAULT_FLUSH_MS
);

/** Hard cap on distinct fingerprints tracked/reported per session. */
export const ERROR_LOG_MAX_PER_SESSION = parsePositiveInt(
  import.meta.env.VITE_ERROR_LOG_MAX_PER_SESSION,
  DEFAULT_MAX_PER_SESSION
);

/** Per-report `stack` truncation budget, in bytes. */
export const ERROR_LOG_MAX_STACK_BYTES = parsePositiveInt(
  import.meta.env.VITE_ERROR_LOG_MAX_STACK_BYTES,
  DEFAULT_MAX_STACK_BYTES
);

/** A batch whose encoded size exceeds this is dropped rather than sent. */
export const ERROR_LOG_MAX_BATCH_BYTES = parsePositiveInt(
  import.meta.env.VITE_ERROR_LOG_MAX_BATCH_BYTES,
  DEFAULT_MAX_BATCH_BYTES
);
